/**
 * NYSE regular-session calendar. Pure — no network, no I/O, no clock reads
 * except the `new Date()` defaults.
 *
 * Wall-clock arithmetic goes through `Intl.DateTimeFormat` with
 * `timeZone: 'America/New_York'`, so the DST transitions (2026-03-08 and
 * 2026-11-01) are handled by the runtime's tz database rather than by us.
 */

export const ET = 'America/New_York';

const OPEN_MIN = 9 * 60 + 30; // 09:30 ET
const REGULAR_CLOSE_MIN = 16 * 60; // 16:00 ET
const EARLY_CLOSE_MIN = 13 * 60; // 13:00 ET

/**
 * NYSE full closures, keyed by calendar year.
 *
 * D9 finding 5: this was a single flat 2026 set applied to whatever year it was
 * handed, so on 2027-01-01 the market would have looked open and every session
 * boundary after it would have been a quiet fabrication. A year with no table
 * is a year we do not know, and {@link holidaysFor} says so out loud.
 */
const HOLIDAYS: Readonly<Record<number, ReadonlySet<string>>> = {
  // Source: NYSE published holiday calendar.
  // Independence Day 2026 falls on a Saturday, so the market closes Friday 3 Jul.
  2026: new Set([
    '2026-01-01', // New Year's Day
    '2026-01-19', // Martin Luther King Jr. Day
    '2026-02-16', // Washington's Birthday
    '2026-04-03', // Good Friday
    '2026-05-25', // Memorial Day
    '2026-06-19', // Juneteenth
    '2026-07-03', // Independence Day (observed, 4 Jul is a Saturday)
    '2026-09-07', // Labor Day
    '2026-11-26', // Thanksgiving Day
    '2026-12-25', // Christmas Day
  ]),
  // ⚠️ VERIFY BEFORE 2027. Derived from the NYSE's published rules (third
  // Mondays, last Monday of May, Good Friday off a computed Easter, weekend
  // holidays observed on the adjacent weekday) rather than read off an NYSE
  // notice, because the 2027 notice did not exist when this was written.
  // Check it against nyse.com/markets/hours-calendars and delete this note.
  2027: new Set([
    '2027-01-01', // New Year's Day (Friday)
    '2027-01-18', // Martin Luther King Jr. Day
    '2027-02-15', // Washington's Birthday
    '2027-03-26', // Good Friday (Easter is 28 Mar)
    '2027-05-31', // Memorial Day
    '2027-06-18', // Juneteenth (observed, 19 Jun is a Saturday)
    '2027-07-05', // Independence Day (observed, 4 Jul is a Sunday)
    '2027-09-06', // Labor Day
    '2027-11-25', // Thanksgiving Day
    '2027-12-24', // Christmas Day (observed, 25 Dec is a Saturday)
  ]),
};

/**
 * 1:00 pm ET closes, keyed by year.
 *
 * 2026 has only two: the usual 3 Jul half day is a full closure because 4 Jul
 * lands on a Saturday. (The dev spec says "three early closes" — that is the
 * typical year, not 2026. See notes/day-log.) 2027 has only one: 24 Dec is the
 * observed Christmas closure and 4 Jul falls on a Sunday, which the NYSE
 * observes without an early close on the preceding Friday.
 *
 * A year absent from {@link HOLIDAYS} has no entry here either, and is rejected
 * before this table is ever consulted.
 */
const EARLY_CLOSES: Readonly<Record<number, ReadonlySet<string>>> = {
  2026: new Set([
    '2026-11-27', // Day after Thanksgiving
    '2026-12-24', // Christmas Eve
  ]),
  2027: new Set([
    '2027-11-26', // Day after Thanksgiving
  ]),
};

/** Years this calendar can answer for, ascending. */
export const CONFIGURED_YEARS: readonly number[] = Object.keys(HOLIDAYS)
  .map(Number)
  .sort((a, b) => a - b);

/**
 * Thrown when the calendar is asked about a year it has no holiday table for.
 *
 * Guessing is the one thing that must not happen here: a fabricated session
 * boundary silently mis-dates every gap signal measured against it, and nothing
 * downstream can tell the difference. Adding the year to {@link HOLIDAYS} is a
 * two-minute fix; a year of quietly wrong closes is not.
 */
export class CalendarNotConfigured extends Error {
  readonly year: number;

  constructor(year: number) {
    super(
      `market calendar has no holiday table for ${year} ` +
        `(configured: ${CONFIGURED_YEARS.join(', ')}). ` +
        `Add it to HOLIDAYS/EARLY_CLOSES in packages/core/src/market-calendar.ts.`,
    );
    this.name = 'CalendarNotConfigured';
    this.year = year;
  }
}

/** The holiday set for a year, or a loud error. */
export function holidaysFor(year: number): ReadonlySet<string> {
  const set = HOLIDAYS[year];
  if (!set) throw new CalendarNotConfigured(year);
  return set;
}

/** The early-close set for a year, or a loud error. */
export function earlyClosesFor(year: number): ReadonlySet<string> {
  holidaysFor(year); // same configured-year gate, one source of truth
  return EARLY_CLOSES[year] ?? new Set();
}

/** Back-compat aliases. Prefer {@link holidaysFor}. */
export const HOLIDAYS_2026 = holidaysFor(2026);
export const EARLY_CLOSES_2026 = earlyClosesFor(2026);

/** The year in a 'YYYY-MM-DD' string. */
function yearOf(date: string): number {
  return Number(date.slice(0, 4));
}

export interface EtParts {
  /** 'YYYY-MM-DD' in ET. */
  date: string;
  year: number;
  month: number; // 1-12
  day: number;
  /** Minutes since ET midnight. */
  minutes: number;
  /** 0 = Sunday .. 6 = Saturday. */
  weekday: number;
}

const PARTS_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: ET,
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  weekday: 'short',
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Break an instant into ET wall-clock parts. */
export function etParts(d: Date = new Date()): EtParts {
  const raw: Record<string, string> = {};
  for (const p of PARTS_FMT.formatToParts(d)) raw[p.type] = p.value;

  const year = Number(raw['year']);
  const month = Number(raw['month']);
  const day = Number(raw['day']);
  // Intl renders ET midnight as hour "24" in some ICU versions; normalise.
  const hour = Number(raw['hour']) % 24;
  const minute = Number(raw['minute']);
  const weekday = WEEKDAY_INDEX[raw['weekday'] ?? ''] ?? 0;

  return {
    date: `${pad4(year)}-${pad2(month)}-${pad2(day)}`,
    year,
    month,
    day,
    minutes: hour * 60 + minute,
    weekday,
  };
}

/**
 * Day-of-week for a 'YYYY-MM-DD' ET calendar date. Parsed as UTC noon so no
 * timezone offset can roll it onto a neighbouring day.
 */
export function weekdayOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
}

/**
 * True for Mon-Fri that is not a full closure. Half days are trading days.
 * `weekday` is derived from `date` when omitted; callers that already have it
 * (from `etParts`, or from walking a cursor) pass it to skip the parse.
 *
 * Throws {@link CalendarNotConfigured} for a year with no holiday table. The
 * weekend check is deliberately *after* that gate: a Saturday in 2030 is still
 * a year we cannot answer for, and returning `false` would let a caller walk a
 * cursor through an unknown year picking up plausible-looking weekdays.
 */
export function isTradingDay(date: string, weekday: number = weekdayOf(date)): boolean {
  const holidays = holidaysFor(yearOf(date));
  if (weekday === 0 || weekday === 6) return false;
  return !holidays.has(date);
}

/**
 * Minute-of-day the session ends on this date. 13:00 on an early-close day.
 * Throws {@link CalendarNotConfigured} for an unconfigured year.
 */
export function closeMinuteFor(date: string): number {
  return earlyClosesFor(yearOf(date)).has(date) ? EARLY_CLOSE_MIN : REGULAR_CLOSE_MIN;
}

/**
 * Is the NYSE regular session open at this instant? Pre/post market is closed.
 * Throws {@link CalendarNotConfigured} once the clock reaches a year with no
 * holiday table — see the class doc for why that beats a plausible `false`.
 */
export function isMarketOpen(d: Date = new Date()): boolean {
  const p = etParts(d);
  if (!isTradingDay(p.date, p.weekday)) return false;
  return p.minutes >= OPEN_MIN && p.minutes < closeMinuteFor(p.date);
}

/** Minutes after the closing bell that the close snapshot is taken. */
export const SNAPSHOT_DELAY_MIN = 5;

/**
 * How long the snapshot stays due. A minutely check that misses its exact
 * minute — a restart, a slow tick, a busy event loop — still catches the
 * session inside this window; the caller remembers which session it has done.
 */
export const SNAPSHOT_WINDOW_MIN = 15;

/**
 * The UTC instant a session's closing bell falls on. 20:00 UTC in EDT, 21:00
 * in EST, 18:00 on an early close in EST — which is exactly why nothing here
 * may hard-code a UTC hour.
 */
export function closeInstant(date: string): Date {
  return etWallToUtc(date, closeMinuteFor(date));
}

/**
 * The session whose close snapshot is due at `d`, or null.
 *
 * A fixed `5 20 * * *` cron meant 16:05 ET for eight months of the year and
 * 15:05 ET for the other four — mid-session, where the job would have read a
 * live intraday price and stored it as the *previous* session's close. The
 * trigger is derived from the calendar instead, so it tracks DST and early
 * closes without anyone remembering to change it.
 *
 * Throws {@link CalendarNotConfigured} for an unconfigured year; the caller in
 * `apps/agent/src/index.ts` logs that and skips the tick.
 */
export function isJustAfterClose(
  d: Date = new Date(),
  delayMin: number = SNAPSHOT_DELAY_MIN,
  windowMin: number = SNAPSHOT_WINDOW_MIN,
): string | null {
  const p = etParts(d);
  if (!isTradingDay(p.date, p.weekday)) return null;
  const due = closeMinuteFor(p.date) + delayMin;
  return p.minutes >= due && p.minutes < due + windowMin ? p.date : null;
}

/**
 * The most recent session whose closing bell has already rung at `d`,
 * as an ET date string. This is the session a gap signal measures against.
 */
export function lastCloseSession(d: Date = new Date()): string {
  const p = etParts(d);
  if (isTradingDay(p.date, p.weekday) && p.minutes >= closeMinuteFor(p.date)) {
    return p.date;
  }
  let cursor = p.date;
  let weekday = p.weekday;
  for (let i = 0; i < 14; i++) {
    ({ date: cursor, weekday } = previousDay(cursor, weekday));
    if (isTradingDay(cursor, weekday)) return cursor;
  }
  throw new Error(`no trading day within 14 days before ${p.date}`);
}

/** The next 09:30 ET opening bell strictly after `d`. */
export function nextOpen(d: Date = new Date()): Date {
  const p = etParts(d);
  let cursor = p.date;
  let weekday = p.weekday;

  if (isTradingDay(cursor, weekday) && p.minutes < OPEN_MIN) {
    return etWallToUtc(cursor, OPEN_MIN);
  }
  for (let i = 0; i < 14; i++) {
    ({ date: cursor, weekday } = nextDay(cursor, weekday));
    if (isTradingDay(cursor, weekday)) return etWallToUtc(cursor, OPEN_MIN);
  }
  throw new Error(`no trading day within 14 days after ${p.date}`);
}

// --- date-string arithmetic (calendar days, no tz involved) ---

function previousDay(date: string, weekday: number): { date: string; weekday: number } {
  return { date: shiftDate(date, -1), weekday: (weekday + 6) % 7 };
}

function nextDay(date: string, weekday: number): { date: string; weekday: number } {
  return { date: shiftDate(date, 1), weekday: (weekday + 1) % 7 };
}

function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = Date.UTC(y, m - 1, d) + days * 86_400_000;
  const dt = new Date(t);
  return `${pad4(dt.getUTCFullYear())}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/**
 * ET wall-clock date + minute-of-day -> the UTC instant.
 *
 * Two passes: guess that the wall time is UTC, measure the zone offset at that
 * guess, correct, then re-measure in case the correction crossed a DST edge.
 */
export function etWallToUtc(date: string, minuteOfDay: number): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const naive = Date.UTC(y, m - 1, d, Math.floor(minuteOfDay / 60), minuteOfDay % 60);
  const firstPass = naive - offsetMs(new Date(naive));
  return new Date(naive - offsetMs(new Date(firstPass)));
}

/** ET offset from UTC at this instant, in ms (negative west of Greenwich). */
function offsetMs(d: Date): number {
  const p: Record<string, string> = {};
  for (const part of PARTS_FMT.formatToParts(d)) p[part.type] = part.value;
  const asIfUtc = Date.UTC(
    Number(p['year']),
    Number(p['month']) - 1,
    Number(p['day']),
    Number(p['hour']) % 24,
    Number(p['minute']),
    Number(p['second']),
  );
  return asIfUtc - d.getTime();
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function pad4(n: number): string {
  return String(n).padStart(4, '0');
}
